import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Bell, LogOut, Menu, ArrowLeftRight, Moon, Search, User as UserIcon, Settings } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { useUIStore } from '@/stores/ui.store';
import { usePermissions } from '@/lib/permissions';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { EUserRole, getRoleDisplayName } from '@/types';
import { useNotifications } from '@/features/notifications/hooks/useNotifications';
import { NotificationsPanel } from '@/features/notifications/components/NotificationsPanel';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { getNavItemsForUser } from './sidebar-config';
import { OfflineIndicator } from '@/components/shared/offline-indicator';
import { InstallPWAPrompt } from '@/components/shared/install-pwa-prompt';
import { useHierarchyLabelMap } from '@/features/operations/hooks/useHierarchyLabels';
import { authService } from '@/features/auth/services/auth.service';

export function Header() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const activeRole = useAuthStore((s) => s.activeRole);
  const activePortal = useAuthStore((s) => s.activePortal);
  const switchRole = useAuthStore((s) => s.switchRole);
  const logout = useAuthStore((s) => s.logout);
  const { can } = usePermissions();
  const queryClient = useQueryClient();
  const handleLogout = useCallback(() => {
    // Best-effort server-side revoke; client-side logout proceeds regardless.
    const token = useAuthStore.getState().accessToken;
    if (token) {
      void authService.logout(token).catch(() => undefined);
    }
    void queryClient.cancelQueries();
    // No clear() here: logout() already resets the cache, and it keeps the
    // workspace identity. Clearing outright dropped that too, so the sign-in
    // screen we navigate to wore the platform's branding until a full reload.
    logout();
    void navigate('/auth/login', { replace: true });
  }, [logout, navigate, queryClient]);
  const toggleSidebar = useUIStore((s) => s.toggleSidebar);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const { unreadCount } = useNotifications();
  const [notifOpen, setNotifOpen] = useState(false);
  const hierarchyLabels = useHierarchyLabelMap();

  // Cmd+K keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
        setSearchQuery('');
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Build searchable nav items from sidebar config
  const searchResults = useCallback(() => {
    if (!searchQuery.trim()) return [];
    const visible = getNavItemsForUser(
      activePortal,
      hierarchyLabels,
      activeRole ?? EUserRole.MINE_DIRECTOR,
      undefined,
      can,
    );
    const results: { label: string; href: string; group: string }[] = [];

    visible.forEach((item) => {
      if (item.children?.length) {
        item.children.forEach((child) => {
          results.push({ label: child.label, href: child.href, group: item.label });
        });
      } else {
        results.push({ label: item.label, href: item.href, group: 'Navigation' });
      }
    });

    const q = searchQuery.toLowerCase();
    return results.filter((r) => r.label.toLowerCase().includes(q) || r.group.toLowerCase().includes(q)).slice(0, 10);
  }, [searchQuery, activePortal, activeRole, hierarchyLabels, can])();

  const handleNavigate = (href: string) => {
    void navigate(href);
    setSearchOpen(false);
    setSearchQuery('');
  };

  return (
    <header className="flex h-14 items-center justify-between border-b bg-background px-4 md:px-6">
      {/* Left: mobile menu + greeting */}
      <div className="flex items-center gap-3">
        <button
          onClick={toggleSidebar}
          className="rounded-md p-2 text-muted-foreground hover:bg-accent lg:hidden"
        >
          <Menu className="size-5" />
        </button>
        <div className="hidden md:block">
          <h2 className="text-sm font-semibold text-text-heading">
            Welcome back, {user?.firstName}
          </h2>
        </div>
      </div>

      {/* Center: role badge (if multi-role) */}
      {user && user.roles.length > 1 && (
        <div className="hidden md:flex items-center gap-2">
          <Badge variant="outline" className="bg-primary-50 text-primary border-primary/20 gap-1.5">
            <ArrowLeftRight className="size-3" />
            {getRoleDisplayName(activeRole ?? user.role)}
          </Badge>
        </div>
      )}

      {/* Right: install prompt, offline status, search, notifications, user menu */}
      <div className="flex items-center gap-1">
        {/* PWA install CTA (hidden when not installable / already installed) */}
        <InstallPWAPrompt />

        {/* Offline + sync status — opens the sync drawer on click */}
        <OfflineIndicator />

        {/* Search */}
        <Button variant="ghost" size="icon" className="text-muted-foreground" onClick={() => { setSearchOpen(true); setSearchQuery(''); }} aria-label="Search (Cmd+K)">
          <Search className="size-4" />
        </Button>

        {/* Theme toggle (disabled — dark mode coming soon) */}
        <Button
          variant="ghost"
          size="icon"
          className="text-muted-foreground opacity-50 cursor-not-allowed"
          disabled
          aria-label="Dark mode coming soon"
        >
          <Moon className="size-4" />
        </Button>

        {/* Notification bell */}
        <Sheet open={notifOpen} onOpenChange={setNotifOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="relative text-muted-foreground">
              <Bell className="size-4" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex size-4 items-center justify-center rounded-full bg-destructive text-[9px] font-bold text-white">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </Button>
          </SheetTrigger>
          <SheetContent className="w-full p-0 sm:w-[420px] sm:max-w-[460px] lg:w-[460px]">
            <SheetTitle className="sr-only">Notifications</SheetTitle>
            <NotificationsPanel />
          </SheetContent>
        </Sheet>

        {/* User menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="rounded-full">
              <Avatar className="size-8">
                <AvatarFallback className="bg-primary-50 text-primary text-xs font-semibold">
                  {user?.firstName[0]}{user?.lastName[0]}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium">{user?.firstName} {user?.lastName}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />

            {/* Role switcher */}
            {user && user.roles.length > 1 && (
              <>
                <DropdownMenuLabel className="text-xs text-muted-foreground">Switch Role</DropdownMenuLabel>
                {user.roles.map((role) => (
                  <DropdownMenuItem
                    key={role}
                    onClick={() => {
                      switchRole(role);
                      // switchRole doesn't navigate — land on the new role's dashboard, since the old page may not be accessible to it.
                      void navigate('/admin/dashboard');
                    }}
                    className={role === activeRole ? 'bg-primary-50 text-primary' : ''}
                  >
                    <ArrowLeftRight className="size-4 mr-2" />
                    {getRoleDisplayName(role)}
                    {role === activeRole && <span className="ml-auto text-xs">Active</span>}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
              </>
            )}

            <DropdownMenuItem>
              <UserIcon className="size-4 mr-2" />
              Account
            </DropdownMenuItem>
            <DropdownMenuItem>
              <Settings className="size-4 mr-2" />
              Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
              <LogOut className="size-4 mr-2" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Search dialog (Cmd+K) */}
      <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
        <DialogContent className="p-0 max-w-lg gap-0 overflow-hidden">
          <div className="flex items-center border-b px-4">
            <Search className="size-4 text-text-body shrink-0" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search pages..."
              className="border-0 focus-visible:ring-0 h-12 text-base shadow-none"
              autoFocus
            />
            <kbd className="hidden sm:inline-flex shrink-0 items-center rounded bg-muted px-1.5 py-0.5 text-[10px] font-mono text-text-body">ESC</kbd>
          </div>
          <div className="max-h-[320px] overflow-y-auto p-2">
            {!searchQuery.trim() && (
              <p className="text-sm text-text-body text-center py-8">Start typing to search pages...</p>
            )}
            {searchQuery.trim() && searchResults.length === 0 && (
              <p className="text-sm text-text-body text-center py-8">No results for "{searchQuery}"</p>
            )}
            {searchResults.length > 0 && (
              <div className="space-y-0.5">
                {searchResults.map((result) => (
                  <button
                    key={result.href}
                    onClick={() => handleNavigate(result.href)}
                    className="w-full flex items-center justify-between rounded-lg px-3 py-2.5 text-left hover:bg-accent transition-colors"
                  >
                    <div>
                      <p className="text-sm font-medium text-text-heading">{result.label}</p>
                      <p className="text-xs text-text-body">{result.group}</p>
                    </div>
                    <span className="text-xs text-text-body">↵</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </header>
  );
}
